import { Component, Input, OnChanges, ChangeDetectionStrategy, ChangeDetectorRef, SimpleChanges } from '@angular/core'
import { has } from 'lodash';
import { ColorService, DEFAULT_EXCLUDE_FALSY_TOOGLE, DatesService, Dimension, DynamicDateRange, EMPTY_FALSY_ERROR, MessageService, Metric, Result, TrackingService } from '@hub-components'
import { DEFAULT_STYLE_OPTIONS } from '../default-chart-options'
import { ChartOptions } from '../chart-options'
import { DEFAULT_MAP_TYPE } from '../../../../constants'
import { WidgetStyle, WidgetStyleOptions } from '../../interfaces/widget-style.interface';

const SUPPORTED_INPUTS = [{
  bindTo: 'dimensions',
  label: 'Dimension',
  type: 'dimension'
}, {
  bindTo: 'metrics',
  label: 'Metrics',
  type: 'metric'
}]

const NL_REGIONS_DEFINITIONS = {
  'north brabant': 'Noord-Brabant',
  'north holland': 'Noord-Holland',
  'south holland': 'Zuid-Holland'
}

const BE_REGIONS_DEFINITIONS = {
  'province of namur': 'Namur'
}

const CA_REGIONS_DEFINITIONS = {
  'quebec': 'Québec'
}

const COUNTRIES_DEFINITIONS = {
  'united states': 'United States of America',
  'tanzania': 'United Republic of Tanzania',
  "cote d'ivoire": 'Ivory Coast'
}

const MAP_TO_DEFINITIONS = {
  'countries/nl/nl-all': NL_REGIONS_DEFINITIONS,
  'countries/be/be-all': BE_REGIONS_DEFINITIONS,
  'countries/ca/ca-all': CA_REGIONS_DEFINITIONS
}

@Component({
  selector: 'map-chart',
  template: '<app-map *ngIf="series" [series]="series" [footer]="footer" [footerData]="footerData" [datasetDisplayName]="datasetDisplayName" [chartOptions]="chartOptions" [style]="style"></app-map>',
  styles: [`
    :host {
      height: 100%;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class MapChartComponent implements OnChanges {
  @Input() result: Result
  @Input() dimensions: Dimension[]
  @Input() metrics: Metric[]
  @Input() chartOptions: ChartOptions = {}
  @Input() dashboardInterval: DynamicDateRange;
  @Input() style: WidgetStyle;

  datasetDisplayName: string
  footer: any
  public footerData: any;
  public series: any[];
  private _series: any[] = [];
  private _filteredSeries: any[] = [];
  private excludeFalsy = DEFAULT_EXCLUDE_FALSY_TOOGLE;
  private isFilterEmpty = false;

  static supportedStyle: WidgetStyleOptions[] = DEFAULT_STYLE_OPTIONS;
  static supportedRollups = ['total']
  static supportedInputs = SUPPORTED_INPUTS
  static supportedOptions = [{
    bindTo: 'map',
    label: 'Map',
    type: 'mapSettings',
    options: [{
      bindTo: 'mapType'
    }, {
      bindTo: 'selectDimension'
    }]
  }, {
    bindTo: 'dataOptions',
    label: 'Data',
    type: 'dataOptions',
    options: [{
      bindTo: 'excludeFalsy'
    }]
  }, {
    bindTo: 'title',
    label: 'Title',
    type: 'textFormat',
    options: [{
      bindTo: 'text'
    }, {
      bindTo: 'align'
    }, {
      bindTo: 'size'
    }]
  }, {
    bindTo: 'footer',
    label: 'Footer',
    type: 'footerCheckbox',
    options: [
      {
        bindTo: 'datasetInfo',
      },
      {
        bindTo: 'dateRange'
      }
    ]
  }, {
    bindTo: 'legend',
    label: 'Legend',
    type: 'textFormat',
    options: [{
      bindTo: 'layout'
    }, {
      bindTo: 'position'
    }, {
      bindTo: 'align'
    }]
  }, {
    bindTo: 'dateRange',
    label: 'Date range',
    type: 'dateRange',
    options: [{
      bindTo: 'enabled'
    }, {
      bindTo: 'value'
    }]
  }]

  static queryOverrides = {
    limit: 1000
  }

  constructor(
    private colorService: ColorService,
    private changeDetectorRef: ChangeDetectorRef,
    private messageService: MessageService,
    private trackingService: TrackingService,
    private datesService: DatesService,
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (this.result && this.metrics && this.dimensions) {
      this.datasetDisplayName = <string>this.result.query.schema.displayName

      this._series = this.getSeriesData(false);
      this._filteredSeries = this.getSeriesData(true);
      this.isFilterEmpty = this._filteredSeries.length === 0 && this._series.length !== 0;
      if (this.isFilterEmpty && this.excludeFalsy) {
        this.messageService.info(EMPTY_FALSY_ERROR);
      }
      this.updateSeriesData();

      if (this.chartOptions.dataOptions) {
        if (!this.excludeFalsy && this.isFilterEmpty && this.chartOptions.dataOptions.excludeFalsy) {
          this.messageService.info(EMPTY_FALSY_ERROR);
        }
        if (this.excludeFalsy !== this.chartOptions.dataOptions?.excludeFalsy) {
          this.addTracking('excludeFalsy', changes);
          this.excludeFalsy = this.chartOptions.dataOptions?.excludeFalsy;
        }
        this.updateSeriesData();
      }

      if (this.chartOptions.footer) {
        if (this.footer !== undefined) {
          if (this.footer.dateRange !== this.chartOptions.footer?.dateRange) {
            this.addTracking('footerDateRange', changes);
          }
          if (this.footer.datasetInfo !== this.chartOptions.footer?.datasetInfo) {
            this.addTracking('footerDatasetInfo', changes);
          }
        }

        this.footer = this.chartOptions.footer;
        this.footerData = {
          datasetInfo: this.result.query.schema.displayName,
          dateRange: this.datesService.intervalDateRange(this.dashboardInterval, this.result.query.firstDayOfWeek ? Number(this.result.query.firstDayOfWeek) : undefined),
        };
      }

      if (this.style) {
        if (this.style.baseColor) {
          this.chartOptions.colors = this.colorService.getSimilarColorSeries(this.style.baseColor, 3)
        }
        this.chartOptions.textColor = this.style.textColor
        this.chartOptions.backgroundColor = this.style.backgroundColor
      }
    }
  }

  private getSeriesData(excludeFalsy: boolean) {
    const metricIndex = this.result.metrics.findIndex(m => m.name === this.metrics[0]?.name);
    const dimensionSelected = this.result.dimensions[0];
    const mapSelected = this.chartOptions?.map?.mapType?.value || DEFAULT_MAP_TYPE.value;

    if (dimensionSelected) {
      let metricFormat = this.result.metrics[metricIndex].format;
      metricFormat = typeof metricFormat === 'string' ? { metricFormat } : metricFormat;
      let joinBy = ['name', 'key'];
      const alpha2 = this.result.rows.some(row => has(row.slices[0], 'data.alpha2') && row.slices[0]['data']['alpha2']);
      const code = this.result.rows.some(row => has(row.slices[0], 'data.code') && row.slices[0]['data']['code']);

      if ((alpha2 || code) && (mapSelected === 'custom/world-robinson' || mapSelected === 'custom/asia' || mapSelected === 'custom/europe')) {
        joinBy = ['iso-a2', 'key']
      }

      let key;
      let data = this.result.rows
        .filter(row => excludeFalsy ? !!row.metrics[metricIndex].value : true)
        .map(row => {
          if (mapSelected === 'custom/world-robinson' || mapSelected === 'custom/asia' || mapSelected === 'custom/europe') {
            if (joinBy.includes('iso-a2')) {
              key = row.slices[0]?.data?.alpha2 || row.slices[0]?.data?.code;
            } else {
              key = COUNTRIES_DEFINITIONS[row.slices[0].value.toLowerCase()] || row.slices[0].value
            }
          } else {
            const regions = MAP_TO_DEFINITIONS[mapSelected]
            key = regions && regions[row.slices[0].value.toLowerCase()] || row.slices[0].value
          }
          const value = row.metrics[metricIndex]['value']
          return { key: key, value: value }
        });

      return [{
        data: data,
        name: this.result.metrics[metricIndex].displayName,
        format: metricFormat && metricFormat['format'],
        joinBy: joinBy
      }];
    }
  }

  private updateSeriesData() {
    this.series = this.excludeFalsy ? this._filteredSeries : this._series;
  }

  private addTracking(type: string, changes: SimpleChanges) {
    if (changes?.options) {
      const { currentValue, previousValue } = changes?.options;

      if (type === 'excludeFalsy' && previousValue?.dataOptions && currentValue?.dataOptions) {
        if (previousValue.dataOptions.excludeFalsy !== currentValue.dataOptions.excludeFalsy) {
          this.trackingService.track('Map Chart Widget: Exclude N/A and 0 values', { value: this.chartOptions.dataOptions.excludeFalsy });
        }
      }

      if (previousValue?.footer && currentValue?.footer) {
        if (type === 'footerDateRange' && previousValue?.footer?.dateRange !== currentValue?.footer?.dateRange) {
          this.trackingService.track('Map Chart Widget: Footer DateRange', { value: this.chartOptions.footer.dateRange });
        }
        if (type === 'footerDatasetInfo' && previousValue?.footer?.datasetInfo !== currentValue?.footer?.datasetInfo) {
          this.trackingService.track('Map Chart Widget: Footer Dataset Info', { value: this.chartOptions.footer.datasetInfo });
        }
      }
    }
  }
}
